<template>
  <BittsModal
    v-if="modalVisible"
    class="c-invite-modal"
    title="Invite Partner"
    :width="500"
    :hide-footer="true"
    :no-padding="true"
    :use-mask-to-close="true"
    @closed="modalClosed"
  >
    <div
      class="c-invite-modal__content md:px-24 md:py-0 md:rounded-none md:border-none md:shadow-none"
      v-if="publicInviteCode && publicInviteOrg"
    >
      <div
        class="py-16 w-full rounded-bts-base text-center bg-neutral-100 border-none"
      >
        <BittsAvatar
          :org="publicInviteOrg"
          :show-initials="true"
          shape="square"
          size="large"
        />
        <div
          data-testid="modal-partner-name"
          class="text-neutral-900 font-bold text-m my-8"
        >
          {{ publicInviteOrg?.name || '' }}
        </div>
        <div
          data-testid="modal-partner-text"
          class="text-neutral-500 text-base"
        >
          A partnership request will be sent to
          {{ publicInviteOrg?.name || '' }}.
        </div>
      </div>
      <div v-if="isAdminPanelUser && onBehalfStoresReady" class="mt-16">
        <BittsSelect
          v-model="selectedUserOnBehalfOf"
          :options="usersWithManagePartnershipsPermission"
          :allow-clear="true"
          data-testid="send-invite-on-behalf-to-user-selector"
          placeholder="Select user"
          :form-label="{
            title: 'Send invite on behalf of',
          }"
          option-type="entity"
        />
        <BittsAlert
          v-if="selectedUserOnBehalfOf != null"
          color="warning"
          :message="onBehalfOfAlertMessage"
          class="mt-12"
        />
      </div>
      <BittsDivider class="my-16 hidden md:block" />
      <div class="md:flex md:justify-end mt-16">
        <BittsButton
          :loading="sending"
          type="neutral"
          variant="outline"
          text="Cancel"
          class="mr-8 w-full md:w-auto hidden md:flex"
          @click="cancel"
        />
        <BittsButton
          :loading="sending"
          :disabled="isAdminPanelUser && !selectedUserOnBehalfOf"
          text="Invite"
          class="w-full md:w-auto"
          @click="onSubmit"
        />
      </div>
    </div>
    <BittsEmptyState
      v-else
      title="Trying to use a partner invite link? Ask your partner for their unique invite link or send them an invitation directly through Crossbeam"
      :button="{ text: 'Invite Partner' }"
      svg-name="empty-state-partner"
      class="c-invite-modal__empty-state"
      @click="() => router.push({ name: 'create_proposal' })"
    >
    </BittsEmptyState>
  </BittsModal>
</template>

<script setup lang="ts">
import {
  BittsAlert,
  BittsAvatar,
  BittsButton,
  BittsDivider,
  BittsEmptyState,
  BittsModal,
  BittsSelect,
} from '@crossbeam/bitts';
import { EVENT_SITES } from '@crossbeam/itly';

import { useHead } from '@unhead/vue';
import axios from 'axios';
import { DateTime } from 'luxon';
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import useAuth from '@/composables/useAuth';
import useIteratively from '@/composables/useIteratively';
import { captureException } from '@/errors';
import { ls } from '@/local_storage';
import {
  allReady,
  useFlashesStore,
  usePartnersStore,
  useRolesStore,
  useTeamStore,
} from '@/stores';
import urls from '@/urls';

type InvitePayloadType = {
  public_invite_code: string;
  from_public_invite: boolean;
  on_behalf_of?: number | null;
};

useHead({
  title: 'Invite - Partner - Crossbeam',
  meta: [
    {
      name: 'robots',
      content: 'noindex nofollow',
    },
  ],
});

const { currentOrg, currentUser, isAdminPanelUser } = useAuth();
const route = useRoute();
const router = useRouter();
const partnersStore = usePartnersStore();
const teamStore = useTeamStore();
const rolesStore = useRolesStore();

const onBehalfStoresReady = allReady(teamStore, rolesStore);
const storedInvite = ls.publicInvite.get();
const publicInviteOrg = ref(storedInvite?.org ?? null);
const publicInviteCode = ref(storedInvite?.code ?? '');
const flashesStore = useFlashesStore();
const { iteratively } = useIteratively();

const sending = ref(false);
const modalVisible = ref(true);

const selectedUserOnBehalfOf = ref<number | null>(null);

const authorizationsWithManagePartnerships = computed(() =>
  teamStore.authorizations.filter(({ role_id, user }) => {
    const role = rolesStore.allRolesWithIsCustom.find(
      ({ id }) => id === role_id,
    );
    return (
      role?.permissions.some(
        (permission) => permission.id === 'manage:partnerships',
      ) && user.email !== currentUser.value.email
    );
  }),
);

const usersWithManagePartnershipsPermission = computed(() =>
  authorizationsWithManagePartnerships.value.map(({ user }) => ({
    ...user,
    value: user.id,
    label: `${user.first_name} ${user.last_name}`,
  })),
);

const representedUser = computed(() =>
  usersWithManagePartnershipsPermission.value.find(
    (user) => user.id === selectedUserOnBehalfOf.value,
  ),
);

const onBehalfOfAlertMessage = computed(
  () =>
    `Make sure you have the authorization of ${representedUser.value?.label} to send this invite`,
);
const orgYoungerThanTwoHours = computed(() => {
  const created = DateTime.fromISO(currentOrg.value.created_at);
  const now = DateTime.local();
  return (now.diff(created, 'hours').toObject()?.hours ?? 0) < 2;
});

function clearPublicInvite() {
  ls.publicInvite.set();
  partnersStore.setPublicInviteOrg(null);
  partnersStore.setPublicInviteCode('');
}

async function onSubmit() {
  try {
    sending.value = true;
    const payload: InvitePayloadType = {
      public_invite_code: publicInviteCode.value,
      from_public_invite: true,
    };
    if (isAdminPanelUser.value && selectedUserOnBehalfOf.value != null) {
      payload.on_behalf_of = selectedUserOnBehalfOf.value;
    }
    const response = await axios.post(urls.proposals.create_v0_4, payload);
    const { from } = route.query;
    const iterativelyPayload = {
      invite_type: from === 'home' ? 'QR Scan' : 'Public Invite',
      event_site: EVENT_SITES.PUBLIC_INVITE_MODAL,
    };
    iteratively.userSentPartnerInvite(iterativelyPayload);
    try {
      await partnersStore.refreshPartnersStore();
    } catch (refreshErr) {
      captureException(refreshErr);
    }
    partnersStore.addProposal(response.data);
    clearPublicInvite();
    await stayOrRedirect();
    flashesStore.addSuccessFlash({ message: 'Invitation sent' });
  } catch (err) {
    captureException(err);
    flashesStore.addErrorFlash({ message: 'Failed to send invitation' });
  } finally {
    sending.value = false;
  }
}
function modalClosed() {
  if (publicInviteCode.value && publicInviteOrg.value) {
    clearPublicInvite();
    flashesStore.addSuccessFlash({ message: 'Invitation not sent' });
  }
  stayOrRedirect();
}
async function stayOrRedirect() {
  // setup_menu_hidden is true if the onboarding is completed
  const name = !currentOrg.value.setup_menu_hidden
    ? 'onboarding'
    : orgYoungerThanTwoHours.value || route.query.from === 'home'
      ? 'home'
      : 'partners';
  await router.push({ name });
}
function cancel() {
  if (publicInviteCode.value || publicInviteOrg.value) {
    clearPublicInvite();
  }
  modalVisible.value = false;
  stayOrRedirect();
}
</script>
<style lang="pcss" scoped>
@reference "../../css/theme.css";
.c-invite-modal__close {
  @apply cursor-pointer text-neutral-text-button;
}
.c-invite-modal__content {
  @apply p-16 rounded-lg border border-neutral-border shadow-md;
}
</style>
<style lang="pcss">
@reference "../../css/theme.css";
.c-invite-modal__empty-state.c-bitts-empty-state-large-border {
  @apply border-none!;
}
</style>
